import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { TransitionProps } from "@mui/material/transitions";
import JSONPretty from "react-json-pretty";
import { useModalStyles } from "./useModalStyles";

export type CustomJsonPopUpProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  object?: any;
  alwaysShowContent?: boolean; // New prop to control visibility of JSON content
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const CustomJsonPopUp: React.FC<CustomJsonPopUpProps> = ({
  open,
  onClose,
  title,
  object,
  alwaysShowContent = false, // Default is false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    cancelBtnSx,
  } = useModalStyles();

  const popupData = useMemo(() => {
    return {
      popupTitle: title || "JSON Details",
    };
  }, [title]);

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        keepMounted
        PaperProps={{
          sx: {
            ...paperSx(isMobile),
            width: isMobile ? "100%" : "720px",
          },
        }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <DataObjectIcon sx={{ fontSize: "1.2rem", color: tokens.accent }} />
          </Box>

          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              {popupData.popupTitle}
            </Typography>
            <Typography
              id="alert-dialog-slide-description"
              sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
            >
              Review the JSON response details below.
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            size="small"
            sx={closeIconSx}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: { xs: "58vh", sm: "62vh" },
              p: 1.25,
              backgroundColor: tokens.cardBg,
              borderRadius: "10px",
              color: tokens.textPrimary,
              border: `1px solid ${tokens.border}`,
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
              "& .__json-pretty__": {
                color: tokens.textPrimary,
                background: "transparent",
              },
            }}
          >
            {alwaysShowContent ? (
              <JSONPretty data={object} style={{ fontSize: "1em" }} />
            ) : (
              <>
                <Box
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    color: tokens.textPrimary,
                    mb: isExpanded ? 1 : 0,
                    userSelect: "none",
                  }}
                >
                  <Box component="span" sx={{ mr: 0.75, color: tokens.accent }}>
                    {isExpanded ? "−" : "+"}
                  </Box>
                  <Box component="span">
                    {isExpanded ? "Hide JSON" : "Show JSON"}
                  </Box>
                </Box>
                {isExpanded && (
                  <JSONPretty data={object} style={{ fontSize: "1em" }} />
                )}
              </>
            )}
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <Button
              onClick={handleClose}
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              variant="outlined"
              sx={{
                ...cancelBtnSx,
                px: 3,
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default CustomJsonPopUp;